import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import {
  add,
  format,
  formatDateRange,
  formatDuration,
  intervalToDuration,
  parseDateFromApi
} from '@sequencehq/utils/dates'

type Phase = Dashboardv99990101Api.GetBillingSchedule.BillingSchedulePhase

/**
 * One row of the widget: a phase term, plus the NetSuite Sales Order it synced
 * to (null for a phase with no fixed obligation, which never produces one).
 */
export type SalesOrderRow = {
  key: string
  startDate: string
  endDate?: string
  salesOrder: {
    id: string
    url?: string
    isPending: boolean
  } | null
}

/**
 * Groups a schedule's phases into one row per Sales Order, ordered by term.
 * Grouped by linked SO, not contract tag, so phases sharing an order collapse.
 *
 *   phase A (Jan-Jun) -> SO 42 -+
 *   phase B (Jul-Dec) -> SO 42 -+-> one row, Jan-Dec
 *   phase C (Jan-Dec) -> none    --> one row, no order
 */
export const buildSalesOrderRows = (
  phases: Array<Phase>
): Array<SalesOrderRow> =>
  Object.values(
    [...phases]
      .sort((a, b) => a.startDate.localeCompare(b.startDate))
      .reduce<Record<string, SalesOrderRow>>((rows, phase) => {
        const link = phase.integrationIds?.find(
          integrationId => integrationId.service === 'NetSuite'
        )
        const key = link ? `salesOrder.${link.id}` : `phase.${phase.id}`
        const existingRow = rows[key]

        return {
          ...rows,
          [key]: {
            key,
            salesOrder: link
              ? mergeSalesOrder(existingRow?.salesOrder, link)
              : (existingRow?.salesOrder ?? null),
            startDate: existingRow?.startDate ?? phase.startDate,
            endDate: existingRow
              ? mergeEndDates(existingRow.endDate, phase.endDate)
              : phase.endDate
          }
        }
      }, {})
  )

/** The term's date range and its length, as displayed on a row. */
export const formatSalesOrderTerm = (row: {
  startDate: string
  endDate?: string
}) => {
  const start = parseDateFromApi(row.startDate)
  const end = row.endDate ? parseDateFromApi(row.endDate) : undefined

  if (!start) {
    return { dateRange: '', duration: '' }
  }

  if (!end) {
    return {
      dateRange: `${format(start, 'd MMM yyyy')} onwards`,
      duration: 'Open ended'
    }
  }

  return {
    dateRange: formatDateRange({ from: start, to: end }),
    // A term includes its end date, so measure through to the following day.
    duration: formatDuration(
      intervalToDuration({ start, end: add(end, { days: 1 }) }),
      { format: ['years', 'months', 'days'] }
    )
  }
}

/**
 * Folds another phase's link into the row's order. Phases grouped onto one
 * order are linked separately, so the record URL may sit on only one of them
 * and a phase can still be pending while its siblings have promoted.
 */
const mergeSalesOrder = (
  salesOrder: SalesOrderRow['salesOrder'],
  link: NonNullable<Phase['integrationIds']>[number]
) => ({
  id: link.id,
  url: salesOrder?.url ?? link.url,
  isPending: (salesOrder?.isPending ?? true) && link.isPending
})

/** An evergreen phase in the group leaves the whole group open ended. */
const mergeEndDates = (
  endDate: string | undefined,
  otherEndDate: string | undefined
) => {
  if (!endDate || !otherEndDate) {
    return undefined
  }

  return endDate > otherEndDate ? endDate : otherEndDate
}
